import { useState, useRef, useEffect, useMemo } from "react";
import {
  Reply,
  ReplyAll,
  Forward,
  Trash2,
  Flag,
  Tag,
  FolderInput,
  Paperclip,
  Check,
  ChevronDown,
  ArrowLeft,
  ShieldAlert,
  Eye,
  Download,
  X,
  Send,
  FileEdit,
  Copy,
  KeyRound,
} from "lucide-react";
import { useEmailStore, Email } from "../../stores/emailStore";
import { useFolderStore } from "../../stores/folderStore";
import { useLabelStore } from "../../stores/labelStore";
import { useAuthStore } from "../../stores/authStore";
import { useApi } from "../../hooks/useApi";
import { getApiBaseUrl } from "../../utils/apiConfig";
import { sanitizeHtml } from "../../utils/sanitize";
import { confirmDialog, alertDialog } from "../../stores/dialogStore";
import { refreshFolders } from "../../utils/refreshFolders";
import { deleteEmailsWithUndo } from "../../utils/deleteWithUndo";
import { detectVerificationCode } from "../../utils/otpDetector";
import { saveSingleEmailToCache, getCachedEmailById } from "../../utils/offlineStorage";
import EmailSpinner from "./EmailSpinner";
import ImageContextMenu from "./ImageContextMenu";

interface EmailContentPanelProps {
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  onEditDraft?: (draftEmail: Email) => void;
}

function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-ES", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFileSize(bytes: number): string {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function EmailContentPanel({ onReply, onForward, onReplyAll, onEditDraft }: EmailContentPanelProps) {
  const {
    currentEmail,
    setCurrentEmail,
    toggleFlag,
    moveEmails,
    addLabelToEmails,
    removeLabelFromEmails,
  } = useEmailStore();
  const folders = useFolderStore((s) => s.folders);
  const activeFolderId = useFolderStore((s) => s.activeFolderId);
  const labels = useLabelStore((s) => s.labels);
  const token = useAuthStore((s) => s.token);
  const { post: apiPost, del, get, patch } = useApi();

  const isDraftFolder =
    (currentEmail?.folderId?.toLowerCase().includes("draft") ||
      currentEmail?.folderId?.toLowerCase().includes("borrador") ||
      activeFolderId?.toLowerCase().includes("draft") ||
      activeFolderId?.toLowerCase().includes("borrador"));

  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [fullEmail, setFullEmail] = useState<Email | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [showExternalImages, setShowExternalImages] = useState(false);
  const [trustedSenders, setTrustedSenders] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("mf_trusted_senders") || "[]");
    } catch {
      return [];
    }
  });
  const [copiedCode, setCopiedCode] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ src: string; name: string } | null>(null);
  const [imageContextMenu, setImageContextMenu] = useState<{
    x: number;
    y: number;
    src: string;
    alt: string;
  } | null>(null);

  const labelMenuRef = useRef<HTMLDivElement>(null);
  const moveMenuRef = useRef<HTMLDivElement>(null);
  const emailBodyRef = useRef<HTMLDivElement>(null);

  // Detect verification/bank 2FA codes in subject or body
  const detectedOtp = useMemo(() => {
    if (!currentEmail) return null;
    const bodyText = fullEmail?.body || currentEmail.body || currentEmail.snippet || "";
    return detectVerificationCode(bodyText, currentEmail.subject);
  }, [currentEmail, fullEmail]);

  const handleCopyOtp = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (labelMenuRef.current && !labelMenuRef.current.contains(e.target as Node)) {
        setShowLabelMenu(false);
      }
      if (moveMenuRef.current && !moveMenuRef.current.contains(e.target as Node)) {
        setShowMoveMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch full email content when currentEmail changes
  useEffect(() => {
    if (!currentEmail) {
      setFullEmail(null);
      setShowExternalImages(false);
      return;
    }

    const senderEmail = currentEmail.from?.email?.toLowerCase() || "";
    if (trustedSenders.includes(senderEmail)) {
      setShowExternalImages(true);
    } else {
      setShowExternalImages(false);
    }

    let isCurrent = true;
    const abortController = new AbortController();

    const fetchFullEmail = async () => {
      setLoadingFull(true);
      setFullEmail(null);
      try {
        const folder = currentEmail.folderId || "INBOX";
        const emailId = currentEmail.id;
        const res = await get<any>(
          `/mail/mails/${encodeURIComponent(folder)}/${emailId}`,
          undefined,
          { signal: abortController.signal }
        );

        // Si el usuario ya cambió a otro correo mientras este descargaba, ignorar la respuesta
        if (!isCurrent) return;

        // La API devuelve el objeto email directamente o dentro de { email }
        const emailData = res?.email || res;
        if (emailData && (emailData.uid || emailData.id)) {
          const normalized: Email = {
            ...currentEmail,
            id: String(emailData.uid || emailData.id || emailId),
            folderId: folder,
            from: Array.isArray(emailData.from)
              ? { name: emailData.from[0]?.name || "", email: emailData.from[0]?.address || emailData.from[0]?.email || "" }
              : (typeof emailData.from === "object" ? { name: emailData.from?.name || "", email: emailData.from?.email || emailData.from?.address || "" } : currentEmail.from),
            to: Array.isArray(emailData.to)
              ? emailData.to.map((t: any) => ({ name: t.name || "", email: t.address || t.email || "" }))
              : currentEmail.to,
            cc: Array.isArray(emailData.cc)
              ? emailData.cc.map((c: any) => ({ name: c.name || "", email: c.address || c.email || "" }))
              : currentEmail.cc,
            subject: emailData.subject || currentEmail.subject,
            snippet: emailData.snippet || currentEmail.snippet,
            body: emailData.bodyText || emailData.body || "",
            bodyHtml: emailData.bodyHtml || undefined,
            date: emailData.date || currentEmail.date,
            isRead: true,
            isFlagged: emailData.isFlagged ?? currentEmail.isFlagged,
            labels: Array.isArray(emailData.labels) ? emailData.labels : currentEmail.labels,
            attachments: Array.isArray(emailData.attachments) ? emailData.attachments : currentEmail.attachments,
            headers: emailData.headers || currentEmail.headers,
          };

          if (!isCurrent) return;
          setFullEmail(normalized);

          // Actualizar el store global para que reply/forward tengan el contenido completo
          useEmailStore.getState().setCurrentEmail(normalized);

          // Guardar en caché IndexedDB para lectura offline
          saveSingleEmailToCache(folder, normalized);

          // Si el correo no estaba leído, marcarlo como leído en el store y en el servidor
          if (!currentEmail.isRead) {
            useEmailStore.getState().markAsRead([emailId]);
            patch(`/mail/mails/${encodeURIComponent(folder)}/${emailId}/flags`, {
              read: true,
            }).catch((err) => console.error("Error al marcar como leído:", err));
            refreshFolders(token);
          }
        }
      } catch (err: any) {
        if (err?.name === "AbortError" || !isCurrent) return;
        // Fallback a IndexedDB en modo offline
        try {
          const folder = currentEmail.folderId || "INBOX";
          const cached = await getCachedEmailById(folder, currentEmail.id);
          if (cached && isCurrent) {
            setFullEmail({
              id: cached.uid,
              folderId: cached.folderId,
              from: cached.from,
              to: cached.to,
              cc: cached.cc,
              bcc: cached.bcc,
              subject: cached.subject,
              snippet: cached.snippet || "",
              body: cached.body || "",
              bodyHtml: cached.bodyHtml,
              date: cached.date,
              isRead: !!cached.isRead,
              isFlagged: !!cached.isFlagged,
              labels: cached.labels || [],
              attachments: cached.attachments,
              headers: cached.headers,
            });
          }
        } catch {
          // silent
        }
      } finally {
        if (isCurrent) {
          setLoadingFull(false);
        }
      }
    };

    fetchFullEmail();

    return () => {
      isCurrent = false;
      abortController.abort();
    };
  }, [currentEmail?.id, currentEmail?.folderId, get, patch, token, trustedSenders]);

  const sanitizedContent = useMemo(() => {
    const rawHtml = fullEmail?.bodyHtml || currentEmail?.bodyHtml;
    if (!rawHtml) return null;
    const res = sanitizeHtml(rawHtml, {
      allowExternalImages: showExternalImages,
      attachments: fullEmail?.attachments || currentEmail?.attachments,
    });
    // Si el HTML resultante queda vacío tras la sanitización, devolver null para activar el fallback a texto plano
    const textOnly = res.html.replace(/<[^>]*>/g, '').trim();
    if (!textOnly && !res.html.includes('<img')) {
      return null;
    }
    return res;
  }, [fullEmail?.bodyHtml, currentEmail?.bodyHtml, fullEmail?.attachments, showExternalImages]);

  // Interceptar clicks y contextmenu sobre imágenes dentro del cuerpo del mensaje
  useEffect(() => {
    const el = emailBodyRef.current;
    if (!el || !currentEmail) return;

    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.tagName.toLowerCase() === "img") {
        const img = target as HTMLImageElement;
        const rawSrc = img.getAttribute("src") || img.getAttribute("data-blocked-src") || "";
        if (rawSrc && !rawSrc.includes("data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==")) {
          e.preventDefault();
          e.stopPropagation();
          const alt = img.getAttribute("alt") || img.getAttribute("title") || "imagen";
          setImageContextMenu({
            x: e.clientX,
            y: e.clientY,
            src: rawSrc,
            alt,
          });
        }
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.tagName.toLowerCase() === "img") {
        const img = target as HTMLImageElement;
        const rawSrc = img.getAttribute("src") || "";
        if (rawSrc && !rawSrc.includes("data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==")) {
          const alt = img.getAttribute("alt") || img.getAttribute("title") || "imagen";
          setPreviewImage({
            src: rawSrc,
            name: alt,
          });
        }
      }
    };

    el.addEventListener("contextmenu", handleContextMenu);
    el.addEventListener("click", handleClick);

    return () => {
      el.removeEventListener("contextmenu", handleContextMenu);
      el.removeEventListener("click", handleClick);
    };
  }, [sanitizedContent?.html, currentEmail]);

  if (!currentEmail) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-[var(--bg-primary)] p-6 select-none">
        <div className="text-center text-[var(--text-muted)] flex flex-col items-center">
          <EmailSpinner size={40} off className="mb-2" />
          <p className="text-xs">Sin selección</p>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    const ok = await confirmDialog({
      title: "¿Mover a Papelera?",
      message: "¿Estás seguro de que deseas eliminar este correo? Podrás deshacerlo si te arrepientes.",
      confirmText: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    const folder = currentEmail.folderId;
    const uid = currentEmail.id;
    deleteEmailsWithUndo(
      [currentEmail],
      () =>
        del(`/mail/mails/${encodeURIComponent(folder)}/${uid}`).catch((err) => {
          console.error("Failed to delete email:", err);
        })
    );
    setCurrentEmail(null);
  };

  const handleToggleFlag = async () => {
    toggleFlag(currentEmail.id);
    try {
      await patch(
        `/mail/mails/${encodeURIComponent(currentEmail.folderId)}/${currentEmail.id}/flags`,
        { flagged: !currentEmail.isFlagged }
      );
    } catch (err) {
      console.error("Failed to flag email:", err);
    }
  };

  const handleToggleLabel = async (labelId: string) => {
    const hasLabel = currentEmail.labels?.includes(labelId);
    if (hasLabel) {
      removeLabelFromEmails([currentEmail.id], labelId);
    } else {
      addLabelToEmails([currentEmail.id], labelId);
    }
    try {
      if (hasLabel) {
        await del(`/labels/${labelId}/emails/${currentEmail.id}`);
      } else {
        await apiPost(`/labels/${labelId}/emails`, { emailId: currentEmail.id });
      }
    } catch (err) {
      console.error("Failed to toggle label:", err);
    }
  };

  const handleMoveToFolder = async (targetFolderId: string) => {
    const sourceFolder = currentEmail.folderId;
    const uid = currentEmail.id;
    moveEmails([uid], targetFolderId);
    setShowMoveMenu(false);
    try {
      await patch(`/mail/mails/${encodeURIComponent(sourceFolder)}/${uid}/move`, {
        destFolder: targetFolderId,
      });
    } catch (err) {
      console.error("Failed to move email:", err);
    }
  };

  const trustSender = () => {
    const senderEmail = currentEmail.from?.email?.toLowerCase();
    if (!senderEmail) return;
    const updated = [...trustedSenders, senderEmail];
    setTrustedSenders(updated);
    localStorage.setItem("mf_trusted_senders", JSON.stringify(updated));
    setShowExternalImages(true);
  };

  const isSenderTrusted = trustedSenders.includes(
    currentEmail.from?.email?.toLowerCase() || ""
  );

  const displayAttachments = (fullEmail?.attachments || currentEmail.attachments || []).filter(
    (att: any) => !att.isInline
  );

  const openImagePreview = (att: any) => {
    if (att.contentBase64) {
      setPreviewImage({
        src: `data:${att.contentType};base64,${att.contentBase64}`,
        name: att.filename,
      });
    } else {
      downloadAttachment(att);
    }
  };

  const downloadAttachment = async (att: any) => {
    if (att.contentBase64) {
      const link = document.createElement("a");
      link.href = `data:${att.contentType};base64,${att.contentBase64}`;
      link.download = att.filename;
      link.click();
      return;
    }
    try {
      const folder = encodeURIComponent(currentEmail.folderId || "INBOX");
      const uid = currentEmail.id;
      const res = await fetch(
        `${getApiBaseUrl()}/mail/mails/${folder}/${uid}/attachments/${encodeURIComponent(att.filename)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!res.ok) throw new Error("Error al descargar");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = att.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alertDialog(err.message || "Error al descargar archivo", "Descarga fallida");
    }
  };

  const downloadImageSrc = async (src: string, filename: string) => {
    try {
      if (src.startsWith("data:")) {
        const link = document.createElement("a");
        link.href = src;
        link.download = filename;
        link.click();
        return;
      }

      const res = await fetch(src, { mode: "cors" });
      if (!res.ok) throw new Error("No se pudo obtener la imagen");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback directo por si CORS previene fetch del blob
      const link = document.createElement("a");
      link.href = src;
      link.target = "_blank";
      link.download = filename;
      link.click();
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-[var(--bg-primary)] overflow-hidden">
      {/* Top bar with back button for mobile (Sticky Header) */}
      <div className="sticky top-0 z-20 flex items-center gap-3 px-3.5 sm:px-4 pt-[max(0.625rem,env(safe-area-inset-top))] sm:pt-[max(0.75rem,env(safe-area-inset-top))] pb-2.5 sm:pb-3 border-b border-[var(--border-color)] bg-[var(--bg-primary)]/95 backdrop-blur-md flex-shrink-0 shadow-2xs">
        <button
          onClick={() => setCurrentEmail(null)}
          className="p-2 sm:p-1.5 hover:bg-[var(--bg-secondary)] active:bg-[var(--bg-tertiary)] transition-colors lg:hidden text-[var(--text-primary)] -ml-1 flex-shrink-0"
          title="Volver a la lista"
          aria-label="Volver a la lista de correos"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base sm:text-[17.5px] font-semibold text-[var(--text-primary)] truncate">
              {currentEmail.subject || "(Sin asunto)"}
            </h2>
            {/* Label Badges with quick-remove */}
            {labels
              .filter((l) => currentEmail.labels?.includes(l.id))
              .map((label) => (
                <span
                  key={label.id}
                  className="inline-flex items-center gap-1 text-2xs font-bold pl-2 pr-1 py-0.5 text-white tracking-wide uppercase shadow-2xs group"
                  style={{
                    backgroundColor: label.color,
                  }}
                >
                  <span>{label.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleLabel(label.id);
                    }}
                    className="p-0.5 hover:bg-black/20 text-white/80 hover:text-white transition-colors"
                    title={`Quitar etiqueta ${label.name}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            {formatFullDate(currentEmail.date)}
          </p>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-24 sm:pb-20 divide-y divide-[var(--border-color)]">
        {/* From / To headers */}
        <div className="px-4 sm:px-5 py-3.5 sm:py-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-accent/15 text-accent flex items-center justify-center font-bold text-sm flex-shrink-0">
              {(currentEmail.from.name || currentEmail.from.email || "?")
                .charAt(0)
                .toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-1.5">
                <span className="text-[15.5px] font-semibold text-[var(--text-primary)]">
                  {currentEmail.from.name || currentEmail.from.email}
                </span>
                <span className="text-[13px] text-[var(--text-muted)] truncate select-all">
                  &lt;{currentEmail.from.email}&gt;
                </span>
              </div>
              <div className="text-[13px] text-[var(--text-muted)] mt-0.5 flex flex-wrap gap-x-1">
                <span className="font-medium text-[var(--text-secondary)]">Para:</span>
                {currentEmail.to && currentEmail.to.length > 0 ? (
                  currentEmail.to.map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-1 select-all">
                      {r.name && r.name !== r.email ? (
                        <span>
                          {r.name} <span className="text-[var(--text-muted)] opacity-80">&lt;{r.email}&gt;</span>
                        </span>
                      ) : (
                        <span>{r.email}</span>
                      )}
                      {i < currentEmail.to.length - 1 && <span>,</span>}
                    </span>
                  ))
                ) : (
                  <span>Mí</span>
                )}
              </div>
              {currentEmail.cc && currentEmail.cc.length > 0 && (
                <div className="text-[13px] text-[var(--text-muted)] mt-0.5 flex flex-wrap gap-x-1">
                  <span className="font-medium text-[var(--text-secondary)]">Cc:</span>
                  {currentEmail.cc.map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-1 select-all">
                      {r.name && r.name !== r.email ? (
                        <span>
                          {r.name} <span className="text-[var(--text-muted)] opacity-80">&lt;{r.email}&gt;</span>
                        </span>
                      ) : (
                        <span>{r.email}</span>
                      )}
                      {i < currentEmail.cc!.length - 1 && <span>,</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* World-Class Smart OTP / Verification Code Banner */}
        {detectedOtp && (
          <div className="mx-4 sm:mx-5 my-3 p-3 bg-accent/10 border border-accent/30 flex items-center justify-between gap-3 animate-fadeIn">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 bg-accent flex items-center justify-center text-white flex-shrink-0">
                <KeyRound size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-2xs uppercase tracking-wider font-bold text-accent">
                  Código de verificación detectado
                </p>
                <p className="text-lg font-mono font-bold tracking-widest text-[var(--text-primary)] select-all leading-tight">
                  {detectedOtp.code}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleCopyOtp(detectedOtp.code)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-accent hover:bg-accent-hover active:bg-accent-hover text-white text-xs font-semibold transition-all active:scale-95 shadow-sm flex-shrink-0"
              title="Copiar código al portapapeles"
            >
              {copiedCode ? (
                <>
                  <Check size={14} className="text-white" />
                  <span>¡Copiado!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Copiar código</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Gmail-style External Images Warning Banner */}
        {sanitizedContent?.hasExternalImages && !showExternalImages && (
          <div className="px-4 sm:px-5 py-2.5 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 text-[var(--text-secondary)]">
              <ShieldAlert size={15} className="text-accent flex-shrink-0" />
              <span>Las imágenes externas no se muestran para proteger tu privacidad.</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowExternalImages(true)}
                className="px-3 py-1.5 sm:px-2.5 sm:py-1 bg-[var(--bg-primary)] border border-[var(--border-color)] text-accent font-medium hover:bg-accent/10 transition-colors shadow-2xs"
              >
                Mostrar imágenes
              </button>
              {!isSenderTrusted && (
                <button
                  onClick={trustSender}
                  className="px-2.5 py-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  Confiar siempre
                </button>
              )}
            </div>
          </div>
        )}

        {/* Attachments Section */}
        {displayAttachments.length > 0 && (
          <div className="px-4 sm:px-5 py-3 bg-[var(--bg-secondary)]/50">
            <div className="flex items-center gap-2 mb-2">
              <Paperclip size={14} className="text-[var(--text-muted)]" />
              <span className="text-[13px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">
                Archivos adjuntos ({displayAttachments.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {displayAttachments.map((att: any, i: number) => {
                const isImage = att.contentType?.startsWith("image/");
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 border border-[var(--border-color)] bg-[var(--bg-primary)] hover:border-accent/40 transition-all text-xs shadow-2xs group"
                  >
                    {isImage ? (
                      <button
                        onClick={() => openImagePreview(att)}
                        className="flex items-center gap-1.5 text-accent hover:underline font-medium"
                        title="Ver imagen"
                      >
                        <Eye size={14} />
                        <span className="truncate max-w-[180px] text-[var(--text-primary)]">
                          {att.filename}
                        </span>
                      </button>
                    ) : (
                      <span className="truncate max-w-[180px] font-medium text-[var(--text-primary)]">
                        {att.filename}
                      </span>
                    )}

                    <span className="text-2xs text-[var(--text-muted)] font-mono">
                      {formatFileSize(att.size)}
                    </span>

                    <button
                      onClick={() => downloadAttachment(att)}
                      className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-accent transition-colors ml-1"
                      title="Descargar archivo"
                      aria-label={`Descargar archivo ${att.filename}`}
                    >
                      <Download size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Email Body */}
        <div className="px-4 sm:px-5 py-5 sm:py-6">
          {loadingFull ? (
            <div className="flex flex-col items-center gap-3 text-sm text-[var(--text-muted)] py-12 justify-center">
              <EmailSpinner size={40} />
              <span>Cargando correo...</span>
            </div>
          ) : sanitizedContent?.html ? (
            <div
              ref={emailBodyRef}
              className="email-content email-rendered-body text-[15.4px] text-[var(--text-primary)] leading-relaxed overflow-x-auto select-text"
              dangerouslySetInnerHTML={{
                __html: sanitizedContent.html,
              }}
            />
          ) : (
            <pre className="email-rendered-body text-[16.94px] text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap font-sans select-text">
              {fullEmail?.body || currentEmail.body}
            </pre>
          )}
        </div>
      </div>

      {/* Action Footer Bar (Sticky Mobile & Desktop Toolbar with Safe Area) */}
      <div className="sticky bottom-0 z-20 flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]/95 backdrop-blur-md shadow-lg pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {isDraftFolder ? (
          <button
            onClick={() => onEditDraft && onEditDraft(fullEmail || currentEmail)}
            className="flex items-center gap-1.5 px-4 py-2 sm:py-1.5 text-sm sm:text-[13px] font-semibold bg-accent text-white hover:bg-accent-hover active:bg-accent-hover transition-colors shadow-sm"
          >
            <Send size={15} />
            Continuar redactando / Enviar
          </button>
        ) : (
          <>
            <button
              onClick={onReply}
              className="flex items-center gap-1.5 px-3.5 sm:px-3 py-2 sm:py-1.5 text-sm sm:text-[13px] font-semibold sm:font-medium bg-accent text-white hover:bg-accent-hover active:bg-accent-hover transition-colors shadow-sm"
              title="Responder únicamente al remitente (R)"
            >
              <Reply size={15} />
              Responder
            </button>
            <button
              onClick={onReplyAll}
              className="flex items-center gap-1.5 px-3.5 sm:px-3 py-2 sm:py-1.5 text-sm sm:text-[13px] font-medium border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] active:bg-[var(--bg-tertiary)] transition-colors shadow-2xs"
              title="Responder a todos los destinatarios y copias (Shift+R)"
            >
              <ReplyAll size={15} />
              <span className="hidden sm:inline">Responder a todos</span>
              <span className="sm:hidden">Todos</span>
            </button>
            <button
              onClick={onForward}
              className="flex items-center gap-1.5 px-3 sm:px-2.5 py-2 sm:py-1.5 text-sm sm:text-[13px] font-medium border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] active:bg-[var(--bg-tertiary)] transition-colors"
              title="Reenviar este correo (F)"
            >
              <Forward size={15} />
              <span className="hidden sm:inline">Reenviar</span>
            </button>
          </>
        )}

        {/* Labels Menu */}
        <div className="relative" ref={labelMenuRef}>
          <button
            onClick={() => {
              setShowLabelMenu(!showLabelMenu);
              setShowMoveMenu(false);
            }}
            className="flex items-center gap-1 px-3 sm:px-2.5 py-2 sm:py-1.5 text-sm sm:text-xs border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] active:bg-[var(--bg-tertiary)] transition-colors"
          >
            <Tag size={14} />
            <span className="hidden sm:inline">Etiquetas</span>
            <ChevronDown size={11} />
          </button>
          {showLabelMenu && (
            <div className="absolute bottom-full left-0 mb-1.5 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-lg min-w-[160px] py-1 overflow-hidden">
              {labels.map((label) => (
                <button
                  key={label.id}
                  onClick={() => handleToggleLabel(label.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors text-left"
                  style={{
                    backgroundColor: currentEmail.labels.includes(label.id) ? `${label.color}15` : undefined,
                  }}
                >
                  <span
                    className="w-2.5 h-2.5 flex-shrink-0"
                    style={{ backgroundColor: label.color }}
                  />
                  <span
                    className="font-medium"
                    style={{
                      color: currentEmail.labels.includes(label.id) ? label.color : "var(--text-primary)",
                    }}
                  >
                    {label.name}
                  </span>
                  {currentEmail.labels.includes(label.id) && (
                    <Check
                      size={12}
                      className="ml-auto flex-shrink-0"
                      style={{ color: label.color }}
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Move Menu */}
        <div className="relative" ref={moveMenuRef}>
          <button
            onClick={() => {
              setShowMoveMenu(!showMoveMenu);
              setShowLabelMenu(false);
            }}
            className="flex items-center gap-1 px-3 sm:px-2.5 py-2 sm:py-1.5 text-sm sm:text-xs border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] active:bg-[var(--bg-tertiary)] transition-colors"
          >
            <FolderInput size={14} />
            <span className="hidden sm:inline">Mover</span>
            <ChevronDown size={11} />
          </button>
          {showMoveMenu && (
            <div className="absolute bottom-full left-0 mb-1.5 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-lg min-w-[160px] py-1 overflow-hidden">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => handleMoveToFolder(folder.id)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-secondary)] transition-colors ${
                    currentEmail.folderId === folder.id
                      ? "text-accent font-medium bg-accent/10"
                      : "text-[var(--text-primary)]"
                  }`}
                >
                  {folder.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Flag */}
        <button
          onClick={handleToggleFlag}
          className={`p-2.5 sm:p-1.5 transition-colors ${
            currentEmail.isFlagged
              ? "text-accent bg-accent/10"
              : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
          }`}
          title={currentEmail.isFlagged ? "Quitar destaque" : "Destacar"}
        >
          <Flag size={16} className={currentEmail.isFlagged ? "fill-accent" : ""} />
        </button>

        {/* Delete */}
        <button
          onClick={handleDelete}
          className="p-2.5 sm:p-1.5 text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 transition-colors ml-auto"
          title="Eliminar"
          aria-label="Mover a Papelera"
        >
          <Trash2 size={17} />
        </button>
      </div>

      {/* Image Lightbox Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] flex flex-col items-center bg-[var(--bg-primary)] border border-[var(--border-color)] overflow-hidden shadow-2xl p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex items-center justify-between px-3 py-2 border-b border-[var(--border-color)] mb-2">
              <span className="text-xs font-medium text-[var(--text-primary)] truncate max-w-md">
                {previewImage.name}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => downloadImageSrc(previewImage.src, previewImage.name.includes(".") ? previewImage.name : `${previewImage.name}.png`)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)] transition-colors shadow-2xs"
                  title="Descargar imagen"
                >
                  <Download size={13} />
                  <span>Descargar</span>
                </button>
                <button
                  onClick={() => setPreviewImage(null)}
                  className="p-1 hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <img
              src={previewImage.src}
              alt={previewImage.name}
              className="max-h-[75vh] max-w-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Image Context Menu */}
      {imageContextMenu && (
        <ImageContextMenu
          x={imageContextMenu.x}
          y={imageContextMenu.y}
          imageSrc={imageContextMenu.src}
          imageAlt={imageContextMenu.alt}
          onClose={() => setImageContextMenu(null)}
          onPreview={(src, name) => setPreviewImage({ src, name })}
          onDownload={(src, name) => downloadImageSrc(src, name)}
        />
      )}
    </div>
  );
}
